/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AbstractCheckboxListElement } from './abstract-checkbox-list-element.component';
import { CheckboxListModelService } from './checkbox-list-model.service';

@Component({
	selector: 'checkbox-set',
	templateUrl: './checkbox-set.component.html',
	styleUrls: ['./checkbox-set.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxSetComponent extends AbstractCheckboxListElement implements OnInit, OnDestroy {
	private idsSubject$ = new BehaviorSubject<string[]>(null);
	public ids$ = this.idsSubject$.asObservable();

	public indeterminate$ = new BehaviorSubject<boolean>(false);

	constructor(checkboxListModel: CheckboxListModelService) {
		super(checkboxListModel);
	}

	// Optional: instances of a <checkbox-set> that don't specify an [ids] attribute
	// (or which specify a null-ish [ids] value)
	// default to being associated with the ids of ALL of the checkbox-list-items in the list.
	@Input() public set ids(newIds: string[]) {
		this.idsSubject$.next(newIds);
	}

	protected createFormControl() {
		this.checkboxFormControl = new FormControl<boolean>(false);
	}

	protected handleCheckboxChange(newCheckboxValue: boolean) {
		// When this component's checkbox is toggled by the user,
		// change all of the values of the items (via the service)
		// whose IDs are registered with this component:
		this.checkboxListModel.updateSetsItems(this.idsSubject$.value);
	}

	protected finishComponentInitialization() {
		// When the model is changed, update this set's checkbox and its indeterminate state:
		this.checkboxListModel.modelUpdated$.pipe(takeUntil(this.shutdownSubject$)).subscribe(() => {
			this.updateCheckAndIndeterminateStates();
		});

		// When the IDs changed, update this set's checkbox and its indeterminate state:
		this.ids$.pipe(takeUntil(this.shutdownSubject$)).subscribe(() => {
			this.updateCheckAndIndeterminateStates();
		});

		// Also, initialize this set's checkbox and its indeterminate state:
		this.updateCheckAndIndeterminateStates();
	}

	public override checkboxSide$() {
		return this.checkboxListModel.checkboxSide$;
	}

	public handleLabelClick() {
		this.checkboxFormControl.setValue(!this.checkboxFormControl.value);
	}

	private updateCheckAndIndeterminateStates(): void {
		const ids = this.idsSubject$.value;
		if (this.checkboxListModel.allIsCheckedValuesEqual(true, ids)) {
			this.checkboxFormControl.setValue(true, { emitEvent: false });
			this.indeterminate$.next(false);
		} else if (this.checkboxListModel.allIsCheckedValuesEqual(false, ids)) {
			this.checkboxFormControl.setValue(false, { emitEvent: false });
			this.indeterminate$.next(false);
		} else {
			this.indeterminate$.next(true);
		}
	}
}
